import { EnhancedStep, StepProps } from '@clutter/staircase';

import { AddedFlowHelpers } from '@shared/flow/flow_container';

import { SharedCheckoutData, StorageCheckoutData } from './data';

type AddedCheckoutStepProps = {
  enabled: boolean;
  scrollToStep: (
    identifier?: 'next' | 'current',
    mode?: 'top' | 'centered',
  ) => void;
};

// prettier-ignore
type CheckoutStepPropsInner<Data> =
  Omit<StepProps<Data>, 'onChange'>
  & {
      onChange<K extends keyof Data>(
        key: K,
        value: Data[K],
        cb?: (values: Data) => void,
      ): void;
    }
  & Omit<AddedFlowHelpers<StorageCheckoutData>, 'createEvent'>
  & AddedCheckoutStepProps;

type StepDescriptorInner<Data, Props, StepEnum, Extra = {}> = Omit<
  EnhancedStep<Data, Props>,
  'name'
> & {
  name: StepEnum;
  resolveCompleted: (
    arg: {
      values: Data;
      leadToken: string | undefined;
    } & Extra,
  ) => boolean;
};

export enum CheckoutType {
  Standard = 'standard',
  Shareable = 'shareable',
  Reonboarding = 'reonboarding',
  Agent = 'agent',
}

export enum CheckoutVersion {
  Storage = 'two_page_storage',
  /** Challenger storage booking funnel (ENG-1021 / ENG-1019). */
  StorageV2 = 'two_page_storage_v2',
  Moving = 'two_page_moving',
}

/** Storage checkout flow versions (control + challenger). */
export const isStorageCheckoutFlowVersion = (flowVersion: string): boolean =>
  flowVersion === CheckoutVersion.Storage ||
  flowVersion === CheckoutVersion.StorageV2;

export enum StorageCheckoutStep {
  Zip = 'zip',
  PlanSize = 'plan_size',
  Commitment = 'commitment',
  ProtectionPlan = 'protection_plan',
  /** Challenger page-1 pickup address only (control combines with lead on one step). */
  Address = 'address',
  AddressAndLeadInfo = 'address_and_lead_info',
  LeadInfo = 'lead_info',
  Bundle = 'bundle',
  Appointment = 'appointment',
  PackingHelp = 'packing_help',
  Disposal = 'disposal',
  Cart = 'cart',
}

export type SharedCheckoutStepProps =
  CheckoutStepPropsInner<SharedCheckoutData>;

export type StorageCheckoutStepProps =
  CheckoutStepPropsInner<StorageCheckoutData>;

export type StorageStepDescriptor = StepDescriptorInner<
  StorageCheckoutData,
  StorageCheckoutStepProps,
  StorageCheckoutStep,
  {
    disposalEligible: boolean | undefined;
  }
>;
